import { ErrorOutline, ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionProps,
  AccordionSummary,
  AccordionSummaryProps,
  LinearProgress,
} from '@mui/material';
import { mobile } from 'common/styles/breakpoints';
import { DisabledButtonColors } from 'common/styles/button';
import {
  blueShade,
  darkNavy,
  grey,
  lighterBlueShade,
  lighterNavy,
  orange,
  red,
  tableHeaderGrey,
  white,
} from 'common/styles/colors';
import { boxShadow } from 'common/styles/page';
import styled from 'styled-components';

type StatusLabelProps = { isError: boolean };

export const SyncBtnWrapper = styled.div`
  display: flex;
  width: 50%;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;

  @media (max-width: ${mobile}) {
    width: 100%;
    margin-top: 20px;
  }
`;

export const SyncErrorsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const StatusLabel = styled.p<StatusLabelProps>`
  color: ${props => (props.isError ? red : darkNavy)};
  margin: auto 0;
`;

export const SyncButton = styled.button<{
  disabled: boolean;
  isStatusError: boolean;
}>`
  background-color: ${orange};
  font-size: 16px;
  color: ${white};
  border: none;
  border-radius: 4px;
  padding: 5px 15px;
  font-family: KanitLight;

  &:disabled {
    ${DisabledButtonColors}
  }

  &:hover {
    ${props => {
      if (props.isStatusError) {
        return 'cursor: not-allowed;';
      }
      if (props.disabled) {
        return 'cursor: wait;';
      }
      return 'opacity: 0.8; cursor: pointer;';
    }}
  }
`;

export const SyncProgressBar = styled(LinearProgress)`
  width: 100%;
  margin: 10px 0;
`;

export const ErrorAccordion = styled(Accordion).attrs(
  () =>
    ({
      square: true,
      style: { backgroundColor: 'inherit' },
    }) as AccordionProps,
)`
  width: 100%;
`;

export const ErrorIcon = styled(ErrorOutline)`
  margin-right: 3px;
`;

export const AccordionHeader = styled(AccordionSummary).attrs(
  () =>
    ({
      expandIcon: <ExpandMore />,
    }) as AccordionSummaryProps,
)``;

export const ErrorTableStyledWrapper = styled.div`
  width: 100%;

  & > div {
    padding: 0;
    background-color: transparent;
    box-shadow: none;

    & > div.MuiButtonBase-root {
      padding: 0;
    }

    .MuiAccordionDetails-root {
      padding: 0;

      & > div {
        box-shadow: none;
        padding: 0;
      }
    }
  }
`;

export const TitleCase = styled.span<{ isFailed?: boolean }>`
  text-transform: capitalize;
  ${props => (props.isFailed ? `color: ${red};` : '')};
`;

export const ErrorMessage = styled.p`
  color: ${red};
  margin-bottom: 0;
`;

export const SeasonFilterAndBtnWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 30px;
  flex-wrap: wrap;
`;

export const SeasonFilterWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 5px;
  width: 50%;

  span {
    font-family: KanitRegular;
    font-size: 16px;
    color: ${darkNavy};
  }

  @media (max-width: ${mobile}) {
    width: 100%;
  }
`;

export const SeasonModalForm = styled.form`
  position: absolute;
  top: 0px;
  left: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 260px;
  background: ${lighterBlueShade};
  border: 1px solid ${lighterNavy};
  border-radius: 8px;
  z-index: 1;
  box-shadow: ${boxShadow};
  padding: 20px;
`;

export const YearOptionsContainer = styled.div<{
  isError: boolean;
}>`
  display: flex;
  flex-direction: column;
  max-height: 250px;
  border-radius: 4px;
  background: ${white};
  padding: 10px;
  margin: 0 10px;
  overflow-y: auto;

  span {
    text-align: center;
  }

  ${props =>
    props.isError ? `border: 1px solid ${red};` : `border: 1px solid ${grey};`}
`;

export const YearOptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 5px 30px;
`;

const Btn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  border-radius: 4px;
  padding: 5px 10px;
  font-family: KanitLight;
  font-size: 16px;
  gap: 5px;
  margin-top: 10px;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.8;
  }
`;

export const YearCloseBtn = styled(Btn)`
  background: ${grey};
  color: ${white};
  border: none;
`;

export const YearSaveBtn = styled(Btn)`
  background: ${orange};
  color: ${white};
  border: none;
`;

export const SeasonBubble = styled.div`
  display: flex;
  align-items: center;
  height: 30px;
  background: ${white};
  border-radius: 30px;
  border: 1px solid ${grey};

  span {
    padding: 0 10px;
    color: ${tableHeaderGrey};
    font-family: KanitSemiBold;
    font-size: 15px;
  }
`;

export const EditSeasonsBtn = styled.button<{
  disabled: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${white};
  border: 1px solid ${orange};
  border-radius: 30px;
  height: 30px;
  width: 55px;

  :hover {
    background-color: ${props => (props.disabled ? 'none' : blueShade)};
  }

  :disabled {
    background-color: none;
    border-color: ${grey};
    cursor: not-allowed;
  }

  svg {
    color: ${props => (props.disabled ? grey : orange)};
    padding: 1px;
  }
`;

export const ModalError = styled.p`
  font-family: KanitRegular;
  font-size: 14px;
  color: ${red};
  margin: 5px 0 0;
  padding: 0 10px;
  text-align: center;
`;
